import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { KTCard, KTCardBody } from '../../../../../_metronic/helpers';
import { Category } from '../../commission-management/core/_models';
import { useGetCategories } from '../../commission-management/core/_requests';
import {
	SHOP_DEALS_PAGE_SIZE,
	useGetShop,
	useGetShopDeals,
	useSetShopActive,
} from '../api/shops.queries';
import {
	DEAL_STATUS,
	SUBSCRIPTION_STATUS_LABELS,
	Shop,
	ShopDeal,
	formatDate,
	formatDateTime,
	money,
	shopState,
} from '../model/types';
import { ShopsPagination } from './ShopsPagination';

const Stat = ({ label, value }: { label: string; value: string }) => (
	<div className='border border-gray-300 border-dashed rounded py-3 px-4 min-w-125px'>
		<div className='fs-4 fw-bold text-gray-800'>{value}</div>
		<div className='fw-semibold fs-7 text-muted'>{label}</div>
	</div>
);

const Row = ({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) => (
	<div className='d-flex flex-wrap py-2'>
		<div className='text-muted fs-7 min-w-175px'>{label}</div>
		<div className='fw-semibold text-gray-800'>{children}</div>
	</div>
);

/**
 * Подтверждение выключения магазина.
 *
 * С подтверждением, а не одной кнопкой: выключение закрывает витрину для
 * покупателей и шлёт владельцу уведомление в телеграм — промах виден
 * посторонним и молча не откатывается.
 */
const ConfirmToggle = ({
	shop,
	onClose,
}: {
	shop: Shop;
	onClose: () => void;
}) => {
	const { mutate, isLoading } = useSetShopActive(shop.uuid);
	const turningOff = shop.active;

	return (
		<div
			className='modal fade show d-block'
			style={{ background: 'rgba(0,0,0,.4)' }}
		>
			<div className='modal-dialog modal-dialog-centered'>
				<div className='modal-content'>
					<div className='modal-header'>
						<h5 className='modal-title'>
							{turningOff ? 'Выключить магазин' : 'Включить магазин'}
						</h5>
					</div>
					<div className='modal-body'>
						{turningOff ? (
							<>
								<p className='mb-2'>
									«{shop.name}» перестанет открываться у покупателей, а
									владельцу уйдёт уведомление.
								</p>
								<p className='text-muted fs-7 mb-0'>
									Уже идущие сделки не отменяются — закрывается витрина.
								</p>
							</>
						) : (
							<p className='mb-0'>
								«{shop.name}» снова откроется для покупателей.
							</p>
						)}
					</div>
					<div className='modal-footer'>
						<button
							type='button'
							className='btn btn-light'
							onClick={onClose}
							disabled={isLoading}
						>
							Отмена
						</button>
						<button
							type='button'
							className={`btn ${turningOff ? 'btn-danger' : 'btn-success'}`}
							disabled={isLoading}
							onClick={() =>
								mutate(!shop.active, { onSettled: onClose })
							}
						>
							{turningOff ? 'Выключить' : 'Включить'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

const ShopSummary = ({ shop }: { shop: Shop }) => {
	const state = shopState(shop);
	const username = shop.bot_name.replace(/^@/, '');
	const [confirming, setConfirming] = useState(false);

	// Справочник категорий живёт в гаранте, у магазина от него только id.
	// Хук общий с разделом комиссий — тот же ключ react-query, поэтому
	// второго запроса не будет, если справочник уже загружали.
	const { data: categories } = useGetCategories();
	const category = (categories as Category[] | undefined)?.find(
		item => item.id === shop.category_id
	);

	return (
		<>
			<div className='d-flex flex-stack flex-wrap gap-3 mb-6'>
				<div className='d-flex align-items-center gap-3'>
					<h2 className='fw-bold m-0'>{shop.name}</h2>
					<span className={`badge ${state.badge}`}>{state.label}</span>
				</div>
				<div className='d-flex align-items-center gap-3'>
					<button
						type='button'
						className={`btn btn-sm ${
							shop.active ? 'btn-light-danger' : 'btn-light-success'
						}`}
						onClick={() => setConfirming(true)}
					>
						{shop.active ? 'Выключить' : 'Включить'}
					</button>
					<Link to='/apps/shops' className='btn btn-sm btn-light'>
						К списку
					</Link>
				</div>
			</div>

			{confirming && (
				<ConfirmToggle
					shop={shop}
					onClose={() => setConfirming(false)}
				/>
			)}

			<div className='d-flex flex-wrap gap-3 mb-6'>
				<Stat
					label='Товары (активных / всего)'
					value={`${shop.stats.products_active} / ${shop.stats.products_total}`}
				/>
				<Stat
					label='Сделки (закрытых / всего)'
					value={`${shop.stats.deals_finished} / ${shop.stats.deals_total}`}
				/>
				<Stat label='Продано' value={money(shop.stats.sold_cents)} />
			</div>

			<div className='row'>
				<div className='col-lg-6'>
					<Row label='Бот'>
						{username ? (
							<a
								href={`https://t.me/${username}`}
								target='_blank'
								rel='noreferrer'
								className='text-hover-primary'
							>
								@{username}
							</a>
						) : (
							'—'
						)}
					</Row>
					<Row label='Владелец'>
						{shop.owner ? (
							<Link
								to={`/apps/profile/overview/${shop.owner_uuid}`}
								className='text-hover-primary'
							>
								{shop.owner.telegram_id}
								{!shop.owner.active && (
									<span className='badge badge-light-danger ms-2'>
										заблокирован
									</span>
								)}
							</Link>
						) : (
							'—'
						)}
					</Row>
					<Row label='Создан'>{formatDateTime(shop.created_at)}</Row>
					<Row label='Категория'>
						{shop.category_id === null ? (
							<span className='text-warning'>не выбрана</span>
						) : (
							// Пока справочник не приехал — показываем id, а не
							// прочерк: пустое место читается как «категории нет»,
							// а она есть.
							category?.name ?? shop.category_id
						)}
					</Row>
					<Row label='Группа поддержки'>{shop.support_group_id ?? '—'}</Row>
					<Row label='UUID'>
						<span className='text-muted fs-7'>{shop.uuid}</span>
					</Row>
				</div>

				<div className='col-lg-6'>
					<Row label='Тариф'>
						{shop.subscription?.subscription?.name ?? '—'}
						{shop.subscription?.subscription && (
							<span className='text-muted fs-7 ms-2'>
								{money(shop.subscription.subscription.price_cents)} /{' '}
								{shop.subscription.subscription.duration_days} дн.
							</span>
						)}
					</Row>
					<Row label='Состояние подписки'>
						{shop.subscription
							? SUBSCRIPTION_STATUS_LABELS[shop.subscription.status] ??
							  shop.subscription.status
							: '—'}
					</Row>
					<Row label='Оплачена до'>
						{formatDate(shop.subscription?.end_date)}
					</Row>
					<Row label='Автопродление'>
						{shop.subscription?.auto_renewal ? 'включено' : 'выключено'}
					</Row>
					{/* Без этой строки карточка врала: показывала «оплачена до
					    …» и умалчивала, что следующий период уже куплен —
					    оператор шёл к продавцу с «у вас кончается». */}
					{shop.next_subscription && (
						<Row label='Оплачен вперёд'>
							{shop.next_subscription.subscription?.name ?? 'тариф'}
							<span className='text-muted fs-7 ms-2'>
								до {formatDate(shop.next_subscription.end_date)}
							</span>
						</Row>
					)}
					<Row label='История оплат'>
						{/* В карточке владельца, а не здесь: подписки принадлежат
						    человеку, и разложить их по магазинам невозможно —
						    истёкший период связь с магазином не хранит. */}
						<Link
							to={`/apps/profile/overview/${shop.owner_uuid}`}
							className='text-hover-primary'
						>
							в карточке владельца
						</Link>
					</Row>
					<Row label='Тема'>
						{shop.theme
							? `${shop.theme.name}${shop.theme.is_global ? ' (общая)' : ''}`
							: '—'}
					</Row>
				</div>
			</div>

			{shop.description && (
				<div className='pt-4'>
					<div className='text-muted fs-7 mb-1'>Описание</div>
					<div className='text-gray-800' style={{ whiteSpace: 'pre-line' }}>
						{shop.description}
					</div>
				</div>
			)}
		</>
	);
};

const ProductsTable = ({ shop }: { shop: Shop }) => {
	const products = shop.products ?? [];

	if (products.length === 0) {
		return (
			<div className='text-center text-muted py-10'>
				В магазине нет товаров
			</div>
		);
	}

	return (
		<div className='table-responsive'>
			<table className='table align-middle table-row-dashed fs-6 gy-4'>
				<thead>
					<tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
						<th>Товар</th>
						<th>Выдача</th>
						<th className='text-end'>Цена</th>
						<th className='text-end'>В наличии</th>
						<th className='text-end'>Состояние</th>
						<th className='text-end'>Создан</th>
					</tr>
				</thead>
				<tbody className='fw-semibold text-gray-600'>
					{products.map(product => (
						<tr key={product.uuid}>
							<td className='text-gray-800'>{product.name_product}</td>
							<td>
								{product.is_auto_delivery ? (
									<span className='badge badge-light-primary'>Авто</span>
								) : (
									<span className='badge badge-light'>Руками</span>
								)}
							</td>
							<td className='text-end'>{money(product.price_cents)}</td>
							<td className='text-end'>
								{/* У непересчитываемых товаров остаток бессмыслен: они
								    не кончаются, а available_quantity на бэкенде всё
								    равно равен единице. */}
								{product.is_quantifiable ? product.available_quantity : '∞'}
							</td>
							<td className='text-end'>
								{product.is_active ? (
									<span className='badge badge-light-success'>Активен</span>
								) : (
									<span className='badge badge-light-danger'>Скрыт</span>
								)}
							</td>
							<td className='text-end'>{formatDate(product.created_at)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const DealRow = ({ deal }: { deal: ShopDeal }) => {
	const state = DEAL_STATUS[deal.status];
	const products = deal.products ?? [];
	// По ценам на момент продажи, а не по текущим ценам товаров: продавец
	// правит их когда захочет, и суммы прошлых сделок ползли бы задним
	// числом — расходясь с «Продано» в шапке, которое считает база.
	const amount = products.reduce(
		(sum, product) =>
			sum +
			(product.purchases ?? []).reduce(
				(productSum, purchase) => productSum + purchase.price_cents,
				0
			),
		0
	);

	return (
		<tr>
			<td>
				{/* Сама сделка живёт в гаранте, а не в mini-app: здесь у неё
				    только заявка. deal_uuid — её uuid там, по нему и открывается
				    карточка со сторонами, суммами, спором и историей. */}
				<Link
					to={`/apps/deal/${deal.deal_uuid}`}
					className='text-gray-800 text-hover-primary fw-bold'
				>
					{deal.human_id}
				</Link>
			</td>
			<td>
				<span className={`badge ${state?.badge ?? 'badge-light'}`}>
					{state?.label ?? deal.status}
				</span>
			</td>
			<td>
				<div className='d-flex flex-column'>
					{products.map(product => (
						<span key={product.uuid}>
							{product.name_product}
							{product.sold_quantity > 1 && (
								<span className='text-muted'> ×{product.sold_quantity}</span>
							)}
						</span>
					))}
					{products.length === 0 && <span className='text-muted'>—</span>}
				</div>
			</td>
			<td className='text-end'>{money(amount)}</td>
		</tr>
	);
};

const DealsTable = ({ shopUuid }: { shopUuid: string }) => {
	const [page, setPage] = useState(1);
	const { data, isLoading, isError } = useGetShopDeals(shopUuid, page);

	if (isError) {
		return (
			<div className='text-center text-danger py-10'>
				Не удалось загрузить сделки
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className='d-flex justify-content-center py-10'>
				<span className='spinner-border text-primary' role='status' />
			</div>
		);
	}

	const deals = data?.data ?? [];
	if (deals.length === 0) {
		return (
			<div className='text-center text-muted py-10'>
				По магазину ещё не было сделок
			</div>
		);
	}

	return (
		<div className='table-responsive'>
			<table className='table align-middle table-row-dashed fs-6 gy-4'>
				<thead>
					<tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
						<th>ID сделки</th>
						<th>Статус</th>
						<th>Состав</th>
						<th className='text-end'>Сумма</th>
					</tr>
				</thead>
				<tbody className='fw-semibold text-gray-600'>
					{deals.map(deal => (
						<DealRow key={deal.uuid} deal={deal} />
					))}
				</tbody>
			</table>

			<ShopsPagination
				page={page}
				total={data?.total ?? 0}
				pageSize={SHOP_DEALS_PAGE_SIZE}
				onChange={setPage}
			/>
		</div>
	);
};


type Tab = 'products' | 'deals';

/**
 * Карточка магазина: кто владелец, на чём стоит, чем торгует и как идут
 * сделки. Товары и сделки разведены по вкладкам — вместе они дают полотно,
 * в котором ничего не найти.
 */
export const ShopCardPage = () => {
	const { uuid } = useParams<{ uuid: string }>();
	const [tab, setTab] = useState<Tab>('products');
	const { data: shop, isLoading, isError } = useGetShop(uuid);

	if (isLoading) {
		return (
			<KTCard className='w-100'>
				<KTCardBody className='d-flex justify-content-center py-20'>
					<span className='spinner-border text-primary' role='status' />
				</KTCardBody>
			</KTCard>
		);
	}

	if (isError || !shop) {
		return (
			<KTCard className='w-100'>
				<KTCardBody className='py-20 text-center'>
					<div className='text-danger mb-4'>Магазин не найден</div>
					<Link to='/apps/shops' className='btn btn-sm btn-light'>
						К списку
					</Link>
				</KTCardBody>
			</KTCard>
		);
	}

	return (
		<>
			<KTCard className='w-100 mb-6'>
				<KTCardBody className='py-4'>
					<ShopSummary shop={shop} />
				</KTCardBody>
			</KTCard>

			<KTCard className='w-100'>
				<KTCardBody className='py-4'>
					<ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
						<li className='nav-item'>
							<button
								type='button'
								className={`nav-link ${tab === 'products' ? 'active' : ''}`}
								onClick={() => setTab('products')}
							>
								Товары ({shop.stats.products_total})
							</button>
						</li>
						<li className='nav-item'>
							<button
								type='button'
								className={`nav-link ${tab === 'deals' ? 'active' : ''}`}
								onClick={() => setTab('deals')}
							>
								Сделки ({shop.stats.deals_total})
							</button>
						</li>
					</ul>

					{tab === 'products' ? (
						<ProductsTable shop={shop} />
					) : (
						<DealsTable shopUuid={shop.uuid} />
					)}
				</KTCardBody>
			</KTCard>
		</>
	);
};
